import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, TrendingUp, TrendingDown, Sparkles, Trophy, Crown, Medal } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Icon from "@/components/Icon";
import { catColor } from "@/lib/colors";
import { badgeName } from "@/lib/rewards";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Progress() {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [badges, setBadges] = useState([]);
  const [leaders, setLeaders] = useState([]);

  useEffect(() => {
    api.get("/progress/report").then((r) => setReport(r.data)).catch(() => {});
    api.get("/badges").then((r) => setBadges(r.data)).catch(() => {});
    api.get("/leaderboard").then((r) => setLeaders(r.data)).catch(() => {});
  }, []);

  if (!report) return <div className="grid place-items-center py-32"><Loader2 className="animate-spin text-indigo-400" size={32} /></div>;

  return (
    <div>
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-1">Your Progress</h1>
      <p className="text-muted-foreground mb-6">Track your growth into a capable, independent adult.</p>

      {/* Overview stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-7">
        {[
          { label: "Completion", value: `${report.completion}%`, c: "text-indigo-400" },
          { label: "This Week", value: report.weekly.lessons, c: "text-emerald-400" },
          { label: "This Month", value: report.monthly.lessons, c: "text-cyan-400" },
          { label: "Simulations", value: report.simulations_done, c: "text-violet-400" },
        ].map((s) => (
          <div key={s.label} className="glass rounded-2xl p-4 text-center">
            <div className={`text-3xl font-display font-extrabold ${s.c}`}>{s.value}</div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="skills">
        <TabsList className="bg-white/5 rounded-full p-1 mb-5">
          <TabsTrigger value="skills" data-testid="tab-skills" className="rounded-full data-[state=active]:bg-indigo-600 data-[state=active]:text-white">Skills</TabsTrigger>
          <TabsTrigger value="badges" data-testid="tab-badges" className="rounded-full data-[state=active]:bg-indigo-600 data-[state=active]:text-white">Badges</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard" className="rounded-full data-[state=active]:bg-indigo-600 data-[state=active]:text-white">Leaderboard</TabsTrigger>
        </TabsList>

        <TabsContent value="skills" className="space-y-6">
          {/* Strengths / weaknesses */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="glass rounded-3xl p-5">
              <h3 className="font-display font-bold text-sm mb-3 flex items-center gap-2"><TrendingUp size={16} className="text-emerald-400" /> Strengths</h3>
              {report.strengths.length ? report.strengths.map((s) => (
                <div key={s.key} className="flex items-center justify-between py-1.5"><span className="text-sm">{s.name}</span><span className="text-sm font-bold text-emerald-400">{s.progress}%</span></div>
              )) : <p className="text-sm text-muted-foreground">Keep learning to build strengths!</p>}
            </div>
            <div className="glass rounded-3xl p-5">
              <h3 className="font-display font-bold text-sm mb-3 flex items-center gap-2"><TrendingDown size={16} className="text-rose-400" /> Focus Areas</h3>
              {report.weaknesses.map((s) => (
                <div key={s.key} className="flex items-center justify-between py-1.5"><span className="text-sm">{s.name}</span><span className="text-sm font-bold text-rose-400">{s.progress}%</span></div>
              ))}
            </div>
          </div>

          {/* Suggestions */}
          <div className="glass rounded-3xl p-5 border border-indigo-500/20">
            <h3 className="font-display font-bold text-sm mb-3 flex items-center gap-2"><Sparkles size={16} className="text-indigo-400" /> Suggested Improvements</h3>
            <ul className="space-y-2">{report.suggestions.map((s, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-indigo-400">→</span>{s}</li>)}</ul>
          </div>

          {/* Category progress */}
          <div>
            <h3 className="font-display font-bold text-sm mb-3">Category Mastery</h3>
            <div className="space-y-3">
              {report.by_category.map((c) => {
                const col = catColor(c.color);
                return (
                  <div key={c.key} className="flex items-center gap-3">
                    <div className={`h-9 w-9 rounded-xl ${col.bg} grid place-items-center shrink-0`}><Icon name={c.icon} size={17} className={col.text} /></div>
                    <div className="flex-1">
                      <div className="flex justify-between mb-1"><span className="text-sm font-medium">{c.name}</span><span className="text-xs text-muted-foreground font-semibold">{c.completed}/{c.total}</span></div>
                      <div className="h-2 rounded-full bg-white/5 overflow-hidden"><div className={`h-full rounded-full bg-gradient-to-r ${col.grad}`} style={{ width: `${c.progress}%` }} /></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="badges">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {badges.map((b) => (
              <div key={b.id} data-testid={`badge-${b.id}`} className={`glass rounded-2xl p-5 text-center border ${b.owned ? "border-yellow-500/30" : "border-white/5 opacity-50"}`}>
                <div className={`h-14 w-14 rounded-2xl grid place-items-center mx-auto mb-3 ${b.owned ? "bg-gradient-to-br from-yellow-400 to-amber-500 shadow-lg shadow-yellow-500/30" : "bg-white/5"}`}>
                  <Icon name={b.icon} size={26} className={b.owned ? "text-white" : "text-muted-foreground"} />
                </div>
                <h3 className="font-display font-bold text-sm">{b.name}</h3>
                <p className="text-[11px] text-muted-foreground mt-1">{b.desc}</p>
                <span className={`text-[10px] uppercase tracking-wider font-bold mt-2 inline-block ${b.owned ? "text-yellow-400" : "text-muted-foreground"}`}>{b.rarity}</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="leaderboard">
          <div className="space-y-2.5">
            {leaders.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Be the first on the leaderboard — earn some XP!</p>}
            {leaders.map((u) => (
              <div key={u.rank} data-testid={`leader-${u.rank}`} className={`flex items-center gap-3 glass rounded-2xl p-4 ${u.is_me ? "border border-indigo-500/40" : ""}`}>
                <div className={`h-9 w-9 rounded-full grid place-items-center font-display font-bold text-sm shrink-0 ${u.rank === 1 ? "bg-yellow-500/20 text-yellow-400" : u.rank === 2 ? "bg-gray-400/20 text-gray-300" : u.rank === 3 ? "bg-orange-500/20 text-orange-400" : "bg-white/5 text-muted-foreground"}`}>
                  {u.rank <= 3 ? <Medal size={18} /> : u.rank}
                </div>
                <span className="flex-1 font-semibold text-sm">{u.name}{u.is_me && <span className="text-indigo-400 text-xs ml-2">You</span>}</span>
                <span className="text-xs text-muted-foreground">Lv {u.level}</span>
                <span className="text-sm font-bold text-yellow-400">{u.xp} XP</span>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
